/**
 * EntityCardGrid - Responsive grid of entity cards
 * Groups cards by domain with visual separation
 */

import { EntityCard, type AISuggestion } from './EntityCard'
import type { PhaseEntityCard } from '@/hooks/useFlowWorkflow'
import type { EntityDomain } from '@ispot/shared'
import type { EntityTreeInstance } from '@/lib/api/client'

interface EntityCardGridProps {
  cards: PhaseEntityCard[]
  onAddEntity?: (classUri: string) => void
  /** Callback when an instance tag is clicked */
  onInstanceClick?: (instance: EntityTreeInstance) => void
  /** Callback when AI suggestions button is clicked */
  onAISuggestionsClick?: (classUri: string, className: string, domain: EntityDomain) => void
  /** Map of suggestions by classUri */
  suggestionsByClass?: Record<string, AISuggestion[]>
  /** Set of classUris currently loading suggestions */
  loadingClasses?: Set<string>
  /** Callback when a suggestion is accepted */
  onAcceptSuggestion?: (suggestion: AISuggestion, classUri: string) => void
}

export function EntityCardGrid({
  cards,
  onAddEntity,
  onInstanceClick,
  onAISuggestionsClick,
  suggestionsByClass,
  loadingClasses,
  onAcceptSuggestion,
}: EntityCardGridProps) {
  if (cards.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-text-muted">
          No entities configured for this phase
        </p>
      </div>
    )
  }

  // Calculate grid rows based on card count (aim for 3x2 grid)
  const rowCount = Math.ceil(cards.length / 3)

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 h-full"
      style={{
        gridTemplateRows: `repeat(${Math.min(rowCount, 2)}, minmax(0, 1fr))`,
      }}
    >
      {cards.map((card) => (
        <EntityCard
          key={card.classUri}
          classUri={card.classUri}
          className={card.className}
          domain={card.domain}
          description={card.description}
          isRequired={card.isRequired}
          minCount={card.minCount}
          currentCount={card.currentCount}
          instances={card.instances}
          onAddEntity={onAddEntity}
          onInstanceClick={onInstanceClick}
          onAISuggestionsClick={onAISuggestionsClick}
          suggestions={suggestionsByClass?.[card.classUri]}
          isLoadingSuggestions={loadingClasses?.has(card.classUri)}
          onAcceptSuggestion={onAcceptSuggestion}
        />
      ))}
    </div>
  )
}
